import React from "react";
import Text from "../Text/Text";
import { Card, Col, Row, Space } from "antd";
import color from "../../resource/color";
import icon from "../../resource/icon";
import PointProgressBar from "../Progress/PointProgressBar";
import { numberFormatter } from "../../utility/Formatter";
import styled from "styled-components";
import { calculatePercent } from "../../utility/calculateBarTour";
import type { AchieveSpecialReward } from "../../entities/AchievementEntity";

const CustomCard = styled(Card)`
  .ant-card-body {
    padding: 16px;
    overflow: hidden;

    @media (max-width: 768px) {
      padding: 12px;
    }

    @media (max-width: 480px) {
      padding: 8px;
    }
  }
`;

interface Props {
  img?: string;
  mainReward?: {
    targetAmount?: number | string;
    badgeImageUrl?: string;
    badgeMasterUrl?: string;
    mileStoneName: string;
    imageUrl?: string;
    rewardNum?: number;
    isSpecial?: boolean;
  }[];
  specialReward: AchieveSpecialReward[] | undefined;
  progressValue?: number | string;
  remainingToNext?: number | string;
  currentMilestoneName?: string;
  currentRewardNum?: number;
  label?: string;
}

const CardMileStone = ({
  img,
  mainReward = [],
  progressValue = 0,
  remainingToNext,
  currentMilestoneName = "-",
  currentRewardNum,
  specialReward,
  label,
}: Props) => {
  const allRewards = mainReward.map((r, i) => ({
    value: Number(r.targetAmount || 0),
    icon: r.badgeImageUrl ?? r.badgeMasterUrl,
    name: r.mileStoneName,
    targetAmount: r.targetAmount ?? "0",
    mainImg: r.imageUrl,
    rewardNum: r.rewardNum,
    isSpecial: false,
  }));

  const specialRewards =
    specialReward?.map((r: any, i) => ({
      value: Number(r.targetAmount || 0),
      icon: r.badgeImageUrl ?? r.badgeMasterUrl,
      name: r.mileStoneSpecialName,
      targetAmount: r.targetAmount ?? "0",
      mainImg: r.imageUrl,
      rewardNum: r.specialRewardNum,
      isSpecial: true,
    })) || [];

  const mergedRewards = [...allRewards, ...specialRewards];
  const maxTarget = mergedRewards.length > 0 ? Math.max(...mergedRewards.map((r) => r.value)) : 1;
  const isMaxReached = Number(progressValue) >= Number(maxTarget);
  const milestonesForProgress = mergedRewards.map((r) => ({
    value: Math.min((r.value / maxTarget) * 100, 100),
    icon: r.icon,
    name: r.name,
    targetAmount: String(r.targetAmount),
    rewardNum: r.rewardNum,
    isSpecial: r.isSpecial,
  }));

  const targets = mergedRewards.map((r) => r.targetAmount);
  const percent = calculatePercent(Number(progressValue), targets);

  return (
    <CustomCard style={{ background: color.disabledArea, borderRadius: 8 }}>
      <Row wrap gutter={20}>
        <Col xs={24} md={9}>
          <Row align='middle' gutter={8} wrap>
            <Col>
              <img
                src={icon.airplane}
                style={{ width: 44, height: "auto", maxWidth: "100%" }}
                alt='airplane'
              />
            </Col>
            <Col flex='auto'>
              <Text fontFamily='Sarabun' fontSize={18} fontWeight={700}>
                ระดับร้านค้า
              </Text>
            </Col>
          </Row>

          <CustomCard style={{ marginTop: 16, border: 0, borderRadius: 8 }}>
            <Row wrap>
              <Col xs={24} sm={8}>
                <Row align='middle' gutter={8} wrap>
                  <Col xs={6} sm={8}>
                    {img && <img src={img} style={{ width: "100%" }} alt='milestone' />}
                  </Col>
                  <Col xs={18} sm={16}>
                    <Space direction='vertical' size={2}>
                      <Text fontFamily='Sarabun' fontSize={14}>
                        {currentRewardNum && `${label ?? ""} ${currentRewardNum}`}
                      </Text>
                      <Text fontSize={20} fontWeight={700} color='primary'>
                        {currentMilestoneName ?? "ไม่มีระดับ"}
                      </Text>
                    </Space>
                  </Col>
                </Row>
              </Col>

              <Col xs={24} sm={16}>
                <Space direction='vertical' size={1} style={{ textAlign: "end", width: "100%" }}>
                  <Text fontFamily='Sarabun' fontSize={12}>
                    ยอดซื้อสะสม
                  </Text>
                  <Text fontFamily='Sarabun' fontSize={20} fontWeight={700}>
                    {numberFormatter(Number(progressValue), 2)} บาท
                  </Text>
                  {!isMaxReached && remainingToNext && Number(remainingToNext) > 0 ? (
                    <Text fontSize={12} fontFamily='Sarabun'>
                      ซื้อเพิ่มอีก{" "}
                      <Text color='error' fontFamily='Sarabun' fontSize={12}>
                        {numberFormatter(Number(remainingToNext), 2)} บาท
                      </Text>{" "}
                      เพื่อปลดล็อคระดับถัดไป
                    </Text>
                  ) : (
                    <Text color='success' fontFamily='Sarabun' fontSize={12}>
                      สะสมครบทุกความสำเร็จ!
                    </Text>
                  )}
                </Space>
              </Col>
            </Row>
          </CustomCard>
        </Col>

        <Col xs={24} md={15}>
          <PointProgressBar progress={percent} milestones={milestonesForProgress} />
        </Col>
      </Row>
    </CustomCard>
  );
};

export default CardMileStone;
